import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import Sidebar from "./Sidebar";
import API_BASE_URL from "../config";
import { ClipLoader } from "react-spinners";

import "react-datepicker/dist/react-datepicker.css";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatDay = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00Z");
  return { label: DAY_LABELS[d.getUTCDay()], num: d.getUTCDate() };
};

// Owner's own local calendar day, not the UTC calendar day — matters for
// owners east of UTC (e.g. India) viewing this in the early-morning hours,
// where the UTC day hasn't rolled over yet but their local day has.
const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// App-wide convention (see RoomsAvailability.jsx) is dd/MM/yyyy, not US-style.
const formatDDMMYYYY = (isoDateStr) => {
  const [y, m, d] = isoDateStr.split("-");
  return `${d}/${m}/${y}`;
};

const isoToLocalDate = (isoDateStr) => new Date(isoDateStr + "T00:00:00");
const localDateToIso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MyInventory = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const token = storedUser.accessToken || localStorage.getItem("accessToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stayId, setStayId] = useState(null);
  const [stayTitle, setStayTitle] = useState("");
  const [stayOptions, setStayOptions] = useState([]);

  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState({ arrivals: [], departures: [] });
  const [weekStart, setWeekStart] = useState(todayStr());

  const [blockOpen, setBlockOpen] = useState(false);
  const [blockRoomId, setBlockRoomId] = useState(null);
  const [blockStart, setBlockStart] = useState(todayStr());
  const [blockEnd, setBlockEnd] = useState(todayStr());
  const [blockReason, setBlockReason] = useState("unavailable");
  const [blockQuantity, setBlockQuantity] = useState(1);
  const [blockSaving, setBlockSaving] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchSummary = useCallback(async (id, startDate) => {
    const res = await axios.get(
      `${API_BASE_URL}stays/${id}/inventory-summary`,
      { ...authHeaders, params: { start_date: startDate } }
    );
    setSummary(res.data.data);
  }, [token]);

  const fetchToday = useCallback(async (id) => {
    const res = await axios.get(`${API_BASE_URL}bookings/${id}/today-activity`, authHeaders);
    setToday(res.data.data);
  }, [token]);

  // Resolve which properties this owner has, default to the first active one.
  useEffect(() => {
    const init = async () => {
      if (!token || !storedUser?.id) {
        setError("User not logged in");
        setLoading(false);
        return;
      }
      try {
        const staysRes = await axios.post(
          `${API_BASE_URL}stays/owner/list`,
          { owner_id: storedUser.id },
          authHeaders
        );
        const stays = staysRes.data.data || [];
        const activeStays = stays.filter((s) => s.is_active);
        const active = activeStays[0] || stays[0];
        if (!active) {
          setError("No properties found. Add a property first.");
          setLoading(false);
          return;
        }
        setStayOptions((activeStays.length ? activeStays : stays).map((s) => ({ id: s.id, title: s.title })));
        setStayId(active.id);
        setStayTitle(active.title);
        await Promise.all([fetchSummary(active.id, todayStr()), fetchToday(active.id)]);
      } catch (err) {
        console.error("Failed to load inventory", err);
        setError(err.response?.data?.message || "Failed to load inventory");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [switchingProperty, setSwitchingProperty] = useState(false);

  const switchProperty = async (newStayId) => {
    const chosen = stayOptions.find((s) => s.id === newStayId);
    if (!chosen) return;
    setStayId(chosen.id);
    setStayTitle(chosen.title);
    setWeekStart(todayStr());
    setSwitchingProperty(true);
    try {
      await Promise.all([fetchSummary(chosen.id, todayStr()), fetchToday(chosen.id)]);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to load this property's inventory");
    } finally {
      setSwitchingProperty(false);
    }
  };

  const changeWeek = async (deltaDays) => {
    if (!stayId) return;
    const d = new Date(weekStart + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + deltaDays);
    const newStart = d.toISOString().split("T")[0];
    setWeekStart(newStart);
    await fetchSummary(stayId, newStart);
  };

  const handleUpdateStatus = async (bookingId, action) => {
    try {
      await axios.post(
        `${API_BASE_URL}bookings/owner/update-status`,
        { booking_id: bookingId, action },
        authHeaders
      );
      await Promise.all([fetchSummary(stayId, weekStart), fetchToday(stayId)]);
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    }
  };

  const openBlockModal = () => {
    const firstRoom = summary?.roomTypes?.[0];
    setBlockRoomId(firstRoom?.id || null);
    setBlockStart(todayStr());
    setBlockEnd(todayStr());
    setBlockReason("unavailable");
    setBlockQuantity(firstRoom?.capacity || 1);
    setBlockOpen(true);
  };

  const selectBlockRoom = (rt) => {
    setBlockRoomId(rt.id);
    setBlockQuantity(rt.capacity || 1);
  };

  const submitBlock = async () => {
    if (!blockRoomId) return;
    setBlockSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}stays/availability`,
        { roomId: blockRoomId, start_date: blockStart, end_date: blockEnd, is_available: false, reason: blockReason, quantity: blockQuantity },
        authHeaders
      );
      setBlockOpen(false);
      await fetchSummary(stayId, weekStart);
    } catch (err) {
      alert(err.response?.data?.message || "Could not block these dates");
    } finally {
      setBlockSaving(false);
    }
  };

  const [unblocking, setUnblocking] = useState(null); // `${roomId}:${date}` while a request is in flight

  const unblockCell = async (roomId, date) => {
    if (!window.confirm("Unblock this date and make it available again?")) return;
    setUnblocking(`${roomId}:${date}`);
    try {
      await axios.post(
        `${API_BASE_URL}stays/availability`,
        { roomId, start_date: date, end_date: date, is_available: true },
        authHeaders
      );
      await fetchSummary(stayId, weekStart);
    } catch (err) {
      alert(err.response?.data?.message || "Could not unblock this date");
    } finally {
      setUnblocking(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100%" }}>
        <ClipLoader size={45} />
      </div>
    );
  }

  if (error) {
    return (
      <section className="profilecontainer">
        <div className="container"><p className="error-message">{error}</p></div>
      </section>
    );
  }

  const metrics = summary?.metrics || { totalRooms: 0, occupiedToday: 0, availableToday: 0, needsCleaningToday: 0 };
  const roomTypes = summary?.roomTypes || [];

  const REASON_OPTIONS = [
    { value: "cleaning", label: "Cleaning" },
    { value: "personal_use", label: "Personal Use" },
    { value: "unavailable", label: "Not Available" },
  ];

  return (
    <section className="profilecontainer">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="profileinner">
              <div className="row">
                <Sidebar />

                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-9 col-sm-12 col-12">
                  <div className="profilerightouter" style={{ opacity: switchingProperty ? 0.5 : 1, transition: "opacity 0.15s" }}>

                    <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3">
                      <div>
                        <h3 className="mb-0">My Inventory</h3>
                        {stayOptions.length > 1 ? (
                          <select
                            className="form-select form-select-sm mt-1"
                            style={{ width: "auto", display: "inline-block" }}
                            value={stayId || ""}
                            disabled={switchingProperty}
                            onChange={(e) => switchProperty(e.target.value)}
                          >
                            {stayOptions.map((s) => (
                              <option key={s.id} value={s.id}>{s.title}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-muted mb-0">{stayTitle}</p>
                        )}
                      </div>
                      <button className="theme-btn" onClick={openBlockModal}>+ Block Dates</button>
                    </div>

                    {/* METRICS */}
                    <div className="row g-3 mb-3">
                      <div className="col-lg-2 col-md-3 col-6">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-muted small fw-bold">Total Rooms</div>
                          <div className="fs-3 fw-bold">{metrics.totalRooms}</div>
                        </div>
                      </div>
                      <div className="col-lg-2 col-md-3 col-6">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-muted small fw-bold">Occupied Today</div>
                          <div className="fs-3 fw-bold text-primary">{metrics.occupiedToday}</div>
                        </div>
                      </div>
                      <div className="col-lg-2 col-md-3 col-6">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-muted small fw-bold">Available Today</div>
                          <div className="fs-3 fw-bold text-success">{metrics.availableToday}</div>
                        </div>
                      </div>
                      <div className="col-lg-2 col-md-3 col-6">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-muted small fw-bold">Needs Cleaning</div>
                          <div className="fs-3 fw-bold" style={{ color: "#4a90a4" }}>{metrics.needsCleaningToday}</div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-12">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-muted small fw-bold mb-2">By Room Type</div>
                          {roomTypes.map((rt) => (
                            <div key={rt.id} className="d-flex justify-content-between align-items-center small mb-1 gap-2">
                              <span className="fw-semibold text-truncate">{rt.title}</span>
                              <span className="text-nowrap">
                                <span className="text-success fw-bold">{rt.availableToday} open</span>
                                {" / "}
                                <span className="text-primary fw-bold">{rt.occupiedToday} booked</span>
                                {rt.needsCleaningToday > 0 && (
                                  <> {" / "}<span className="fw-bold" style={{ color: "#4a90a4" }}>{rt.needsCleaningToday} cleaning</span></>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="row g-3">

                      {/* TODAY PANEL */}
                      <div className="col-lg-4">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="text-uppercase text-muted small fw-bold mb-2">Today</div>

                          <div className="small fw-bold mb-1">Arriving ({today.arrivals.length})</div>
                          {today.arrivals.length === 0 && <p className="text-muted small">Nobody arriving today.</p>}
                          {today.arrivals.map((b) => (
                            <div key={b.id} className="d-flex align-items-center justify-content-between mb-2 p-2 rounded" style={{ background: "#f4f6f5" }}>
                              <div>
                                <div className="fw-semibold small">{b.guestName}</div>
                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{b.roomTitle}</div>
                              </div>
                              <button className="theme-btn" style={{ padding: "6px 10px", fontSize: "0.75rem" }} onClick={() => handleUpdateStatus(b.id, "checkin")}>Check In</button>
                            </div>
                          ))}

                          <div className="small fw-bold mb-1 mt-3">Leaving ({today.departures.length})</div>
                          {today.departures.length === 0 && <p className="text-muted small">Nobody leaving today.</p>}
                          {today.departures.map((b) => (
                            <div key={b.id} className="d-flex align-items-center justify-content-between mb-2 p-2 rounded" style={{ background: "#f4f6f5" }}>
                              <div>
                                <div className="fw-semibold small">{b.guestName}</div>
                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{b.roomTitle}</div>
                              </div>
                              <button className="theme-btn" style={{ padding: "6px 10px", fontSize: "0.75rem" }} onClick={() => handleUpdateStatus(b.id, "checkout")}>Check Out</button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CALENDAR */}
                      <div className="col-lg-8">
                        <div className="p-3 bg-white rounded shadow-sm h-100">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => changeWeek(-7)}>‹</button>
                            <span className="fw-bold">
                              {summary ? `${formatDDMMYYYY(summary.startDate)} – ${formatDDMMYYYY(summary.endDate)}` : ""}
                            </span>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => changeWeek(7)}>›</button>
                          </div>

                          <div style={{ overflowX: "auto" }}>
                            <table className="table table-borderless mb-2" style={{ minWidth: 560 }}>
                              <thead>
                                <tr>
                                  <th style={{ width: 130 }}></th>
                                  {(summary?.roomTypes?.[0]?.week || []).map((w) => {
                                    const { label, num } = formatDay(w.date);
                                    const isToday = w.date === todayStr();
                                    return (
                                      <th key={w.date} className="text-center small" style={{ color: isToday ? "#c0503a" : "#6b7873" }}>
                                        {label}<br /><span style={{ fontSize: "0.9rem" }}>{num}</span>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {roomTypes.map((rt) => (
                                  <tr key={rt.id}>
                                    <td className="fw-semibold small align-middle">{rt.title}</td>
                                    {rt.week.map((cell) => {
                                      const isToday = cell.date === todayStr();
                                      const isCleaning = cell.state === "blocked" && cell.reason === "cleaning";
                                      const bg = cell.state === "open" ? "#e3f5ec" : cell.state === "booked" ? "#3b6fd6" : isCleaning ? "#4a90a4" : "#c98a2e";
                                      const color = cell.state === "open" ? "#2f9e6b" : "#fff";
                                      const blockedLabel = isCleaning ? "Cleaning" : "Blocked";
                                      const text = cell.state === "open" ? "Open"
                                        : cell.state === "booked" ? (cell.guestInitials || "•")
                                        : cell.remaining > 0 ? `${cell.remaining}/${rt.capacity} open`
                                        : blockedLabel;
                                      const isBlockedCell = cell.state === "blocked";
                                      const cellKey = `${rt.id}:${cell.date}`;
                                      const isUnblocking = unblocking === cellKey;
                                      return (
                                        <td key={cell.date} className="text-center align-middle" style={{ padding: 4 }}>
                                          <div
                                            onClick={isBlockedCell && !isUnblocking ? () => unblockCell(rt.id, cell.date) : undefined}
                                            title={isBlockedCell ? "Click to unblock this date" : undefined}
                                            style={{
                                              background: bg, color, borderRadius: 8, height: 40,
                                              display: "flex", alignItems: "center", justifyContent: "center",
                                              fontWeight: 700, fontSize: "0.72rem",
                                              outline: isToday ? "2px solid #c0503a" : "none", outlineOffset: "-2px",
                                              cursor: isBlockedCell ? "pointer" : "default",
                                              opacity: isUnblocking ? 0.5 : 1
                                            }}>
                                            {isUnblocking ? "…" : text}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="d-flex gap-3 small text-muted flex-wrap">
                            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#2f9e6b", marginRight: 4 }}></span>Open</span>
                            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#3b6fd6", marginRight: 4 }}></span>Booked</span>
                            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#c98a2e", marginRight: 4 }}></span>Blocked by you</span>
                            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#4a90a4", marginRight: 4 }}></span>Cleaning</span>
                          </div>
                          <p className="text-muted mb-0 mt-2" style={{ fontSize: "0.75rem" }}>Click a blocked or cleaning date to make it available again.</p>
                        </div>
                      </div>

                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BLOCK DATES MODAL */}
      {blockOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,30,26,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="bg-white rounded p-4" style={{ width: 380 }}>
            <h5>Block a Room</h5>
            <p className="text-muted small">It won't show as available to guests for these dates.</p>

            <label className="fw-bold small d-block mb-1">Which room?</label>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {roomTypes.map((rt) => (
                <span
                  key={rt.id}
                  onClick={() => selectBlockRoom(rt)}
                  className={`badge rounded-pill px-3 py-2 ${blockRoomId === rt.id ? "bg-dark" : "bg-light text-dark border"}`}
                  style={{ cursor: "pointer" }}
                >
                  {rt.title}
                </span>
              ))}
            </div>

            <label className="fw-bold small d-block mb-1">How many rooms?</label>
            <input
              type="number"
              className="form-control mb-3"
              style={{ width: 120 }}
              min={1}
              max={roomTypes.find((rt) => rt.id === blockRoomId)?.capacity || 1}
              value={blockQuantity}
              onChange={(e) => {
                // The `max` attribute only affects the spinner arrows, not
                // typed input — clamp explicitly so you can't type a number
                // bigger than this room type actually has.
                const cap = roomTypes.find((rt) => rt.id === blockRoomId)?.capacity || 1;
                const val = Math.max(1, Number(e.target.value) || 1);
                setBlockQuantity(Math.min(cap, val));
              }}
            />
            <p className="text-muted small mb-3" style={{ marginTop: -8 }}>
              This room type has {roomTypes.find((rt) => rt.id === blockRoomId)?.capacity || 0} room(s) total — only the number you enter here will be taken off the market.
            </p>

            <label className="fw-bold small d-block mb-1">Which dates?</label>
            <div className="d-flex gap-2 mb-3">
              <DatePicker
                selected={isoToLocalDate(blockStart)}
                onChange={(date) => {
                  const iso = localDateToIso(date);
                  setBlockStart(iso);
                  // Follow the "to" date along by default (most blocks are a
                  // single day) — still freely adjustable right after for a
                  // multi-day range.
                  setBlockEnd(iso);
                }}
                selectsStart
                startDate={isoToLocalDate(blockStart)}
                endDate={isoToLocalDate(blockEnd)}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
              <DatePicker
                selected={isoToLocalDate(blockEnd)}
                onChange={(date) => setBlockEnd(localDateToIso(date))}
                selectsEnd
                startDate={isoToLocalDate(blockStart)}
                endDate={isoToLocalDate(blockEnd)}
                minDate={isoToLocalDate(blockStart)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
            </div>

            <label className="fw-bold small d-block mb-1">Why?</label>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {REASON_OPTIONS.map((opt) => (
                <span
                  key={opt.value}
                  onClick={() => setBlockReason(opt.value)}
                  className={`badge rounded-pill px-3 py-2 ${blockReason === opt.value ? "bg-dark" : "bg-light text-dark border"}`}
                  style={{ cursor: "pointer" }}
                >
                  {opt.label}
                </span>
              ))}
            </div>

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-light flex-fill" onClick={() => setBlockOpen(false)} disabled={blockSaving}>Cancel</button>
              <button className="theme-btn flex-fill" onClick={submitBlock} disabled={blockSaving || !blockRoomId}>
                {blockSaving ? "Blocking…" : "Block These Dates"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MyInventory;
